import clsx from "clsx";
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";
import React, { useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";
import CallMadeIcon from "@material-ui/icons/CallMade";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import routes from "common/routes";
import Dialog from "components/Dialog/Dialog";
import FacilityMarkerIndicator from "components/FacilityMarkerIndicator";
import HorseIcon from "components/Icons/Horse";
import Loader from "components/Loader";
import { useLoggedInUser } from "components/LoggedInUserProvider";
import { useRacehorse360Api } from "hooks/api";
import { useInfiniteScroll } from "hooks/useInfiniteScroll";
import { getWorkoutRequestsQuery } from "pages/DashboardPage/helper";

import useCommonStyles from "../styles";
import useStyles from "./styles";

export interface Props {
  isLoading: boolean;
  totalCount: number;
}

const ScheduledWorkoutsBlock = (props: Props) => {
  const { isLoading, totalCount } = props;

  const commonClasses = useCommonStyles();
  const classes = useStyles();

  const history = useHistory();

  const { currentUser } = useLoggedInUser();

  const tableBodyRef = useRef<HTMLDivElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { useInfiniteListWorkoutRequests } = useRacehorse360Api();

  const {
    isLoading: isPageLoading,
    data,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage
  } = useInfiniteListWorkoutRequests(
    {
      query: getWorkoutRequestsQuery(currentUser.trainerIds),
      pagingOptions: {
        maxResults: 25,
        includeSummary: true
      },
      getOptions: {
        select: [
          "id",
          "date",
          "horse.name",
          "facility.code",
          "facility.name",
          "facility.backgroundColor",
          "facility.foregroundColor",
          "facility.strokeColor"
        ],
        orderBy: ["date"]
      }
    },
    {
      enabled: isModalOpen,
      onError: error => console.error(error)
    }
  );

  const workoutRequests: racehorse360.IWorkoutRequest[] = data?.pages
    ? data.pages.flat()
    : [];

  useInfiniteScroll(
    tableBodyRef,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    true,
    false
  );

  const handleScheduledWorkoutsClick = () => {
    totalCount && setIsModalOpen(true);
  };

  const handleScheduledWorkoutsClose = () => {
    setIsModalOpen(false);
  };

  const handleRowClick = (workoutRequest: racehorse360.IWorkoutRequest) => {
    const horseId = workoutRequest?.horse.id;
    if (horseId) {
      history.push(routes.horseDetails.path.replace(":horseId", horseId));
    }
  };

  return (
    <>
      <Grid
        component={Paper}
        container
        className={clsx(commonClasses.infoPaper, {
          [commonClasses.pointerAvailable]: Boolean(totalCount)
        })}
        onClick={handleScheduledWorkoutsClick}
      >
        {isLoading && <Loader overlay />}
        <Grid item className={commonClasses.badge}>
          <HorseIcon className={commonClasses.badgeIcon} />
        </Grid>
        <Grid item>
          <Grid container className={commonClasses.blockContent}>
            <Grid
              component={Typography}
              item
              className={commonClasses.count}
              xs={12}
            >
              {totalCount}
            </Grid>
            <Grid
              component={Typography}
              item
              className={commonClasses.label}
              xs={12}
            >
              Scheduled Workouts
            </Grid>
          </Grid>
        </Grid>
        {Boolean(totalCount) && (
          <Box className={commonClasses.corner}>
            <CallMadeIcon className={commonClasses.cornerIcon} />
          </Box>
        )}
      </Grid>

      {isModalOpen && (
        <Dialog
          isOpen
          title={`${totalCount} Scheduled Workouts`}
          onClose={handleScheduledWorkoutsClose}
          className={classes.modalRoot}
        >
          {isPageLoading && <Loader className={classes.dialogLoader} />}
          <TableContainer ref={tableBodyRef}>
            <Table>
              <TableBody>
                {workoutRequests?.map((wr, i) => (
                  <TableRow
                    key={wr.id}
                    className={classes.tableWorkoutRow}
                    onClick={() => handleRowClick(wr)}
                  >
                    <TableCell
                      className={clsx(
                        classes.tableWorkoutCell,
                        classes.tableWorkoutName
                      )}
                    >
                      {wr.horse?.name}
                    </TableCell>
                    <TableCell
                      className={clsx(
                        classes.tableWorkoutCell,
                        classes.tableWorkoutFacility
                      )}
                    >
                      <div className={classes.tableWorkoutFacilityCodeBullet}>
                        <FacilityMarkerIndicator
                          facility={wr.facility}
                          size={5}
                        />
                      </div>
                      <span className={classes.tableWorkoutFacilityCode}>
                        {wr.facility?.code}
                      </span>
                    </TableCell>
                    <TableCell
                      className={clsx(
                        classes.tableWorkoutCell,
                        classes.tableWorkoutDate
                      )}
                    >
                      {format(parseISO(wr.date), "M/d/yy")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {isFetchingNextPage && <Loader infinite />}
        </Dialog>
      )}
    </>
  );
};

export default React.memo(ScheduledWorkoutsBlock);
